<script setup lang="ts">
import { DateTime } from 'luxon';
import { ref, type PropType } from 'vue';
import { CustomButton, ButtonType } from '@/components/common/';
import type { CaseNoteHistoryModel, CaseNoteModel } from '@/models/caseNote';

defineProps({
  note: {
    type: Object as PropType<CaseNoteModel>,
    required: true
  }
});

const emit = defineEmits<{
  edit: [note: CaseNoteModel];
}>();

const isExpanded = ref(false);

const DATE_TIME_24H_FORMAT = 'dd MMM yyyy, HH:mm';

const formatDate = (date: Date) => DateTime.fromJSDate(date).toFormat(DATE_TIME_24H_FORMAT);

const formatDateWithoutTimezoneAdjustment = (date: Date) =>
  DateTime.fromJSDate(date, { zone: 'utc' }).toFormat(DATE_TIME_24H_FORMAT);

const getTimingLabel = (note: CaseNoteModel) => {
  if (!note.eventTime && !note.timepoint) return undefined;
  const parts: string[] = [];
  if (note.eventTime) {
    if (note.timepoint) {
      parts.push(formatDateWithoutTimezoneAdjustment(note.eventTime));
    } else {
      parts.push(formatDate(note.eventTime));
    }
  }
  if (note.timepoint) parts.push(`(${note.timepoint})`);
  return parts.join(' ');
};

const getHistoryTimingLabel = (history: CaseNoteHistoryModel) => {
  if (!history.eventTime && !history.timepoint) return undefined;
  const parts: string[] = [];
  if (history.eventTime) {
    if (history.timepoint) {
      parts.push(formatDateWithoutTimezoneAdjustment(history.eventTime));
    } else {
      parts.push(formatDate(history.eventTime));
    }
  }
  if (history.timepoint) parts.push(`(${history.timepoint})`);
  return parts.join(' ');
};

const hasHistory = (note: CaseNoteModel) => {
  if (note.history.length === 0) return false;
  if (note.history.length > 1) return true;
  return note.history[0].operation !== 'INSERT';
};

const formatOperation = (op: string) => {
  if (op === 'INSERT') return 'Created';
  if (op === 'UPDATE') return 'Edited';
  if (op === 'DELETE') return 'Deleted';
  return op;
};

const getOrderedHistory = (note: CaseNoteModel) =>
  [...note.history].sort((a, b) => a.changedAt.getTime() - b.changedAt.getTime());
</script>

<template>
  <div class="note-card">
    <div class="note-header">
      <span class="note-timepoint" v-if="getTimingLabel(note)">
        <FontAwesomeIcon :icon="['far', 'clock']" class="timepoint-icon"/>
        {{ getTimingLabel(note) }}
      </span>
      <div class="note-actions">
        <CustomButton v-if="hasHistory(note)"
          :icon="{ icon: 'clock-rotate-left', style: 'far' }"
          :text="isExpanded ? 'Hide history' : 'History'"
          :type="ButtonType.WITHOUT_OUTLINE"
          @onClick="isExpanded = !isExpanded"/>
        <CustomButton :icon="{ icon: 'pen-to-square', style: 'far' }"
          text="Edit"
          :type="ButtonType.WITHOUT_OUTLINE"
          @onClick="emit('edit', note)"/>
      </div>
    </div>

    <p class="note-content">{{ note.content }}</p>

    <div class="note-history" v-if="hasHistory(note) && isExpanded">
      <div class="history-title">History</div>
      <div class="history-item"
        v-for="item in getOrderedHistory(note)"
        :key="item.id">
        <div class="history-head">
          <span class="history-operation">{{ formatOperation(item.operation) }}</span>
          <span class="history-meta">{{ formatDate(item.changedAt) }}</span>
        </div>
        <div class="history-meta">{{ item.creatorNames }}</div>
        <div class="history-meta" v-if="getHistoryTimingLabel(item)">
          <FontAwesomeIcon :icon="['far', 'clock']" class="history-clock"/>
          {{ getHistoryTimingLabel(item) }}
        </div>
        <p class="history-content">{{ item.content }}</p>
      </div>
    </div>

    <div class="note-footer">
      <span class="empty"></span>
      <span class="note-meta">{{ note.creatorNames }}</span>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use "../../../assets/_variables.module.scss" as *;

.note-card {
  background: #f8f9fc;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  padding: 12px 14px;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;

  &:hover {
    border-color: var(--p-primary-color, #{$primary-color});
    box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08);

    .note-actions {
      opacity: 1;
    }
  }
}

.note-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.note-actions {
  display: flex;
  align-items: center;
  gap: 2px;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.note-timepoint {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: $primary-color;
  background: rgba(0, 102, 255, 0.08);
  padding: 2px 8px;
  border-radius: 12px;

  .timepoint-icon {
    font-size: 10px;
  }
}

.note-content {
  font-size: 14px;
  line-height: 1.5;
  color: var(--color-text);
  margin: 0 0 10px;
  white-space: pre-wrap;
  word-break: break-word;
}

.note-history {
  border-top: 1px dashed var(--color-border);
  margin-top: 8px;
  padding-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.history-title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.5);
}

.history-item {
  padding-left: 10px;
  border-left: 2px solid rgba(0, 102, 255, 0.2);
}

.history-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.history-operation {
  font-size: 12px;
  font-weight: 700;
  color: var(--color-heading);
}

.history-meta {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  gap: 5px;
}

.history-clock {
  font-size: 10px;
}

.history-content {
  margin: 6px 0 0;
  font-size: 13px;
  color: var(--color-text);
  white-space: pre-wrap;
  word-break: break-word;
}

.note-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid var(--color-border);
  padding-top: 8px;
  margin-top: 4px;
}

.note-meta {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.45);
}
</style>
